import CardBody from 'components/card/CardBody'
import CustomCard from 'components/card/CustomCard'
import CustomCardHeader from 'components/card/CustomCardHeader'
import CustomButton, { SecondaryButton } from 'components/CustomButton'
import FormLabel from 'components/form/FormLabel'
import Input from 'components/form/Input'
import { ImageUploadCard } from 'components/ImageUploadCard'
import LabeledSelect from 'components/selects/LabeledSelect'
import { Tag } from 'components/table/Status'
import TableItemLoader from 'components/TableItemLoader'
import ToggleSwitch from 'components/toggleSwitch/ToggleSwitch'
import { isNotEmpty, validateURL } from 'helpers/stringHelper'
import React, { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { updateRemoteConfig } from 'redux/slices/remoteConfigSlice'
import { remoteConfigType } from '../RemoteConfig'

const positionOptions = [
  { value: 'TOP_RIGHT', label: 'Top Right' },
  { value: 'BOTTOM_RIGHT', label: 'Bottom Right' },
  { value: 'TOP_LEFT', label: 'Top Left' },
  { value: 'BOTTOM_LEFT', label: 'Bottom Left' },
  { value: 'CENTER', label: 'Center' },
]

const RoadBlockSection = ({
  selectedPosition,
  setSelectedPosition,
  selectedWebPosition,
  setSelectedWebPosition,
  actionUrl,
  setActionUrl,
  roadblock,
  setRoadblock,
  setConfigType,
  setShowConfirmationModal,
  updateRoadBlockAction,
  fetchRemoteConfigAction,
}) => {
  const [isEditingRoadblock, setIsEditingRoadblock] = useState(false)
  const [invalidUrl, setInvalidUrl] = useState(false)

  const dispatch = useDispatch()
  const { loading, error, remoteConfig } = useSelector(
    (state) => state.remoteConfig,
  )

  const activateDeactivateRoadblock = useCallback(() => {
    if (remoteConfig?.config?.roadblock.road_block) {
      setConfigType(remoteConfigType.roadblock)
      setShowConfirmationModal(true)
    } else {
      updateRoadBlockAction()
    }
  }, [
    remoteConfig?.config?.roadblock.road_block,
    setConfigType,
    setShowConfirmationModal,
    updateRoadBlockAction,
  ])

  const isRoadblockValid = () => {
    return isNotEmpty(roadblock?.app_url) && isNotEmpty(roadblock?.web_url)
  }

  const saveRoadblock = useCallback(() => {
    if (actionUrl) {
      const isValidUrl = validateURL(actionUrl)
      if(!isValidUrl) {
        setInvalidUrl(true)
        return
      }
    }
    dispatch(
      updateRemoteConfig({
        configType: 'Roadblock',
        configPayload: {
          config: {
            roadblock: {
              ...roadblock,
              road_block: roadblock.road_block,
              position: selectedPosition,
              web_position: selectedWebPosition,
              action_url: actionUrl || undefined,
            },
          },
        },
      }),
    )
    setIsEditingRoadblock(false)
    setInvalidUrl(false)
  }, [actionUrl, roadblock, selectedPosition, selectedWebPosition])

  const renderEditImageSection = useCallback(
    (platform) => {
      const isApp = platform === 'app'
      const urlKey = `${platform}_url`
      const position = isApp ? selectedPosition : selectedWebPosition
      const setPosition = isApp ? setSelectedPosition : setSelectedWebPosition

      return (
        <React.Fragment key={platform}>
          <ImageUploadCard
            initialImage={
              roadblock[urlKey] ?? remoteConfig?.config?.roadblock[urlKey]
            }
            title={isApp ? 'App' : 'Web'}
            permanentRemove={false}
            onImageUploaded={(image) =>
              setRoadblock((prev) => ({ ...prev, [urlKey]: image }))
            }
            onImageRemoved={() =>
              setRoadblock((prev) => ({ ...prev, [urlKey]: '' }))
            }
          />
          <div className={isApp ? 'pb-8' : 'pb-4'}>
            <LabeledSelect
              options={positionOptions}
              value={position}
              onChange={setPosition}
              label={`Select position for ${platform}`}
            />
          </div>
        </React.Fragment>
      )
    },
    [
      remoteConfig?.config?.roadblock,
      roadblock,
      selectedPosition,
      selectedWebPosition,
      setRoadblock,
      setSelectedPosition,
      setSelectedWebPosition,
    ],
  )

const handleCancel = () => {
  setIsEditingRoadblock(false)
  setInvalidUrl(false)
  setRoadblock(remoteConfig.config.roadblock)
  setSelectedPosition(remoteConfig.config.roadblock.position)
  setSelectedWebPosition(remoteConfig.config.roadblock.web_position)
  setActionUrl(remoteConfig.config.roadblock.action_url)
}

  return (
    <div className="flex-1">
      <CustomCard>
        <CustomCardHeader contentPosition="none">
          <div className="w-full flex items-center justify-between">
            <h2 className="text-white text-2xl">RoadBlock</h2>
            {!isEditingRoadblock && (
              <ToggleSwitch
                isChecked={remoteConfig?.config?.roadblock?.road_block ?? false}
                onChange={activateDeactivateRoadblock}
              />
            )}
          </div>
        </CustomCardHeader>
        {loading || error.length > 0 ? (
          <TableItemLoader
            loading={loading}
            error={error}
            retry={fetchRemoteConfigAction}
            isCircular={true}
          />
        ) : (
          <CardBody>
            {isEditingRoadblock ? (
              <div>
                {['app', 'web'].map(renderEditImageSection)}
                <Input
                  type="text"
                  color="lightBlue"
                  size="lg"
                  value={actionUrl ?? ''}
                  onChange={(e) => setActionUrl(e.target.value || undefined)}
                  labelComponent={
                    <div className="flex items-center justify-between">
                      <FormLabel label="Action URL (Optional)" />
                    </div>
                  }
                  errorMessage="*Enter valid URL"
                  hideError={!invalidUrl}
                />
                <div className="flex justify-end space-x-4">
                  <SecondaryButton onClick={handleCancel}>
                    Cancel
                  </SecondaryButton>
                  <CustomButton
                    disabled={!isRoadblockValid()}
                    onClick={saveRoadblock}
                  >
                    Save
                  </CustomButton>
                </div>
              </div>
            ) : (
              <div>
                <FormLabel label="App" />
                <div className="w-full h-64 mb-4 border rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
                  <img
                    src={remoteConfig?.config?.roadblock.app_url}
                    className="w-full h-full object-contain"
                    alt="images"
                  />
                </div>
                <div className="flex space-x-1 mb-2">
                  <div className="text-sm">Position:</div>
                  <Tag label={selectedPosition?.label ?? 'Center'} />
                </div>
                <div className="pt-5 mb-4">
                  <FormLabel label="Web" />
                  <div className="w-full h-60 mb-4 border rounded-md overflow-hidden bg-gray-100 flex-shrink-0">
                    <img
                      src={remoteConfig?.config?.roadblock.web_url}
                      className="w-full h-full object-contain"
                      alt="images"
                    />
                  </div>
                  <div className="flex space-x-1">
                    <div className="text-sm">Position:</div>
                    <Tag label={selectedWebPosition?.label ?? 'Center'} />
                  </div>
                </div>
                <div className="flex space-x-1">
                  <label className="text-sm">
                    Action URL: {" "}
                    {actionUrl ? (
                      <Link to={actionUrl} className="text-blue-500 italic">{actionUrl}</Link>
                    ) : (
                      <span className="italic text-gray-400">Not set</span>
                    )}
                  </label>
                </div>
                <div className="flex justify-end items-center mt-8">
                  <CustomButton onClick={() => setIsEditingRoadblock(true)}>
                    Edit
                  </CustomButton>
                </div>
              </div>
            )}
          </CardBody>
        )}
      </CustomCard>
    </div>
  )
}

export default RoadBlockSection
